/**
 * Keep slash / doc-mention / handle menus inside the visible surface.
 *
 * Menus are portaled to document.body with viewport-fixed coordinates.
 * Clamping prefers the doc sheet's scroll body (when present) so the menu
 * doesn't spill below the framed window — not just the browser viewport.
 */

const PAD = 12;

/** True when a portaled editor popup is open — host Esc handlers should defer. */
export const isEditorFloatingMenuOpen = () =>
  !!document.querySelector(
    '.doc-mention-picker, .doc-slash-menu, .callout-menu, .block-handle-menu, .callout-columns-picker, .doc-find-bar, .doc-video-paste-choice, .snippet-block-picker, .snippet-preview-modal, .snippet-backlink-menu, .snippet-source-picker'
  );

const menuBounds = () => {
  const body = document.querySelector('.doc-sheet__body');
  if (body) {
    const r = body.getBoundingClientRect();
    return {
      top: r.top + PAD,
      left: r.left + PAD,
      bottom: r.bottom - PAD,
      right: r.right - PAD,
    };
  }
  return {
    top: PAD,
    left: PAD,
    bottom: window.innerHeight - PAD,
    right: window.innerWidth - PAD,
  };
};

/** Viewport bounds — for handle menus that must stay on screen. */
const viewportBounds = (pad = 8) => ({
  top: pad,
  left: pad,
  bottom: window.innerHeight - pad,
  right: window.innerWidth - pad,
});

/**
 * @param {HTMLElement} menuEl — rendered menu (needs real dimensions)
 * @param {{ x: number, y: number, caretTop?: number }} anchor — viewport
 *   coords; `y` is the menu's preferred top (usually caret bottom + gap)
 */
export const clampFloatingMenuPosition = (menuEl, anchor) => {
  const bounds = menuBounds();
  const w = menuEl.offsetWidth;
  const h = menuEl.offsetHeight;
  if (w <= 0 || h <= 0) return { x: anchor.x, y: anchor.y };

  let x = anchor.x;
  let y = anchor.y;

  // Prefer opening downward; flip above the caret when it would clip
  if (y + h > bounds.bottom) {
    const caretTop = anchor.caretTop ?? y - 8;
    y = caretTop - h - 8;
  }

  y = Math.max(bounds.top, Math.min(y, bounds.bottom - h));
  x = Math.max(bounds.left, Math.min(x, bounds.right - w));

  return { x, y };
};

/**
 * Snapshot a DOMRect into a plain object (safe to keep in state / events).
 * @param {DOMRect | { left: number, top: number, right: number, bottom: number }} rect
 */
export const snapshotRect = (rect) => ({
  left: rect.left,
  top: rect.top,
  right: rect.right,
  bottom: rect.bottom,
  width: rect.width ?? rect.right - rect.left,
  height: rect.height ?? rect.bottom - rect.top,
});

/**
 * Place a fixed (viewport) menu beside a drag-handle anchor.
 *
 * Priority:
 *  1. Ideal seat: left of the handle, top-aligned
 *  2. Stay fully on screen (clamp) — overlapping the handle is fine
 *  3. Flip to the right only when the ideal left seat is fully off-screen
 *
 * @param {HTMLElement} menuEl
 * @param {{ left: number, top: number, right: number, bottom: number }} anchorRect
 * @param {{ gap?: number, fallbackW?: number, fallbackH?: number }} [opts]
 */
export const positionMenuBesideAnchor = (menuEl, anchorRect, opts = {}) => {
  const gap = opts.gap ?? 2;
  const bounds = viewportBounds(8);
  const measuredW = menuEl.offsetWidth || menuEl.getBoundingClientRect().width;
  const measuredH = menuEl.offsetHeight || menuEl.getBoundingClientRect().height;
  // Fallback sizes if layout hasn't flushed — never jump to screen origin
  const w = measuredW > 0 ? measuredW : (opts.fallbackW ?? 190);
  const h = measuredH > 0 ? measuredH : (opts.fallbackH ?? 48);

  if (!anchorRect) {
    return { x: bounds.left, y: bounds.top };
  }

  // Ideal: flush left of the handle, top-aligned.
  let x = anchorRect.left - gap - w;
  let y = anchorRect.top;

  // Flip right only when the whole menu would sit off-screen to the left
  // and the right seat actually fits. Partial left overflow clamps instead
  // (may cover the handle) — better than jumping across the content.
  const rightX = anchorRect.right + gap;
  if (x + w <= bounds.left && rightX + w <= bounds.right) {
    x = rightX;
  }

  x = Math.max(bounds.left, Math.min(x, bounds.right - w));
  y = Math.max(bounds.top, Math.min(y, bounds.bottom - h));

  return { x, y };
};
